import { useCallback } from 'react'
import { useSelector, useStore } from 'react-redux'
import {
  TableStandardSettings,
  TableSettingsPanel,
} from 'apps/studio/components/settings/table/Base/standard'
import { DesignSettingsPanel } from 'apps/studio/components/settings/table/Base/design'
import { settingInputs } from 'apps/studio/components/settings/inputs'
import {
  updateSheet,
  editSheet,
} from 'apps/formsCreationKit/store/actions/sharedActions'
import {
  activeSheetSelector,
  activeTableSheetEntriesSelector,
  sheetsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { withTableSheetDefaults } from 'apps/formsCreationKit/store/reducers/entities/sheets'
import {
  activeStyledPageSelector,
  activeTableLayoutSelector,
} from 'apps/studio/store/selectors/pdf'
import { slugify } from 'apps/builder/lib/utils'
import { pagesActions } from 'apps/studio/store/reducers/entities/pages'
import { layoutsActions } from 'apps/studio/store/reducers/entities/layouts'
const { updatePageStyles } = pagesActions
const { updateLayoutStyles } = layoutsActions

// Redux ignores unregistered action types, so returning this lets us skip a
// dispatch without special-casing every caller (used by read-only rows and
// by early-outs when the required entity is missing).
const NOOP_ACTION = { type: 'studio/tableSettings/noop' }

// Shared frozen empties so `|| EMPTY_*` doesn't allocate a fresh identity every
// render — keeps the `update` callback deps stable when a selector returns null.
const EMPTY_OBJ = Object.freeze({})
const EMPTY_ARR = Object.freeze([])

// display_entry lives on the sheet as { guid }, but SettingsSelect emits the
// raw guid via nestedProp. Wrap it here so the reducer receives the canonical
// shape (or `null` when cleared).
const formatSheetPayload = (property, payload) => {
  if (property !== 'display_entry') return payload
  const value = payload[property]
  if (value && typeof value === 'object' && value.guid) return payload
  return { [property]: value ? { guid: value } : null }
}

const getChildSheetId = (sheet, allSheets) => {
  if (!sheet?.multi_section) return null
  return allSheets.find(s => s.section_id === sheet.multi_section)?.id ?? null
}

const buildUpdateAction = ({
  updateType,
  page,
  sheet,
  layout,
  data,
  property,
  syncChildSheet,
  allSheets,
}) => {
  // sheetEntries always propagates to both parent+detail sheets so entry-level
  // fields stay consistent; `syncChildSheet` is the explicit opt-in for other cases.
  const childSheetId =
    syncChildSheet || updateType === 'sheetEntries'
      ? getChildSheetId(sheet, allSheets)
      : null
  const additionalSheetIds = childSheetId ? [childSheetId] : []

  switch (updateType) {
    case 'sheetEntries':
      return editSheet({
        id: sheet.id,
        additionalSheetIds,
        sheetPayload: formatSheetPayload(property, data),
        updateEntries: true,
      })
    case 'sheet': {
      const sheetPayload = formatSheetPayload(property, data)
      if (additionalSheetIds.length) {
        return editSheet({
          id: sheet.id,
          additionalSheetIds,
          sheetPayload,
        })
      }
      return updateSheet({
        id: sheet.id,
        section_id: sheet.section_id,
        multi_section: sheet.multi_section,
        ...sheetPayload,
      })
    }
    case 'layoutStyles':
      if (!layout?.id) return NOOP_ACTION
      return updateLayoutStyles({ id: layout.id, ...data })
    case 'pageStyles':
      return updatePageStyles({ id: page.id, ...data })
    default:
      return NOOP_ACTION
  }
}

// Returns the object SettingsSelect / SettingsToggle read `data[property]`
// from. Every branch has to expose `property` (either directly or via spread).
export const getData = ({
  updateType,
  page,
  sheet,
  layout,
  pageStyles,
  property,
  mismatchValue,
  tableEntries,
}) => {
  switch (updateType) {
    case 'layoutStyles':
      // Static table typography inherits global/page styles when layout styles
      // are unset; table-specific toggles remain scoped to layout styles only.
      return {
        ...(pageStyles || {}),
        ...(layout?.styles || {}),
        hide_table_title: layout?.styles?.hide_table_title ?? false,
        hide_column_header: layout?.styles?.hide_column_header ?? false,
      }
    case 'layoutMeta':
      // Read-only counters (Columns / Rows) show the current layout size.
      // Read-only until GOC-26582 makes them editable.
      return {
        column_count: layout?.columns?.length || 0,
        row_count: layout?.rows?.length || 0,
      }
    case 'sheetEntries': {
      // Collapse per-entry visibility into a single selection. If entries
      // disagree, surface the mismatch sentinel provided by the config.
      if (!tableEntries?.length) return { [property]: 0 }
      const first = tableEntries[0][property]
      const mismatched = tableEntries.some(e => e[property] !== first)
      return { [property]: mismatched ? mismatchValue : (first ?? 0) }
    }
    case 'sheet':
      // Include the toggle defaults so unset sheets still render the expected
      // switch position — same source of truth as createTableSheets.
      return withTableSheetDefaults(sheet)
    case 'pageStyles':
      return page.styles
    default:
      // Read-only rows (Transpose) still need a value under `property` so the
      // input can render an active state.
      return { [property]: false }
  }
}

const TableSetting = ({ page, sheet, layout, tableEntries, config }) => {
  const store = useStore()
  const {
    label,
    settingType,
    updateType,
    property,
    syncChildSheet,
    mismatchValue,
  } = config

  // Rebuild the closure whenever the target entities change. Depending on the
  // whole objects (not just ids) is intentional — some fields we read (like
  // section_id, multi_section) can flip without the id changing, and pulling
  // `allSheets` from the store at dispatch time saves an extra subscription.
  const update = useCallback(
    data =>
      buildUpdateAction({
        updateType,
        page,
        sheet,
        layout,
        data,
        property,
        syncChildSheet,
        allSheets: sheetsSelector(store.getState()) || [],
      }),
    [store, updateType, sheet, page, layout, property, syncChildSheet]
  )

  const SettingInput = settingInputs[settingType]
  if (!SettingInput) return null

  const data = getData({
    updateType,
    page,
    sheet,
    layout,
    pageStyles: page.styles,
    property,
    mismatchValue,
    tableEntries,
  })
  const settingId = slugify(`${label}-${settingType}-${sheet.id}`)

  return (
    <SettingInput
      guid={page.id}
      id={settingId}
      styleLevel="page"
      data={data}
      portal
      update={update}
      {...config}
    />
  )
}

export const tableSettingsConfig = [
  TableSettingsPanel(TableStandardSettings),
  DesignSettingsPanel,
]

export const TableSettings = ({ activePanels }) => {
  const activeSheet = useSelector(activeSheetSelector) || EMPTY_OBJ
  const activePage = useSelector(activeStyledPageSelector) || EMPTY_OBJ
  const activeLayout = useSelector(activeTableLayoutSelector) || EMPTY_OBJ
  const tableEntries = useSelector(activeTableSheetEntriesSelector) || EMPTY_ARR

  return tableSettingsConfig.map(panel => {
    const { label, settings } = panel
    if (!activePanels.includes(label)) return null

    return (
      <div
        className="gcds--control-panel workspace-panel"
        key={`table-settings-panel-${label}`}
      >
        <div className="panel--info --workspace-panel--header">
          <div className="panel--title --workspace-panel--title">{label}</div>
        </div>
        <div className="panel--content workspace-panel--section field-settings-panel">
          {settings.map(setting => (
            <TableSetting
              key={`${setting.label}-${setting.property || setting.settingType}-${activeSheet.id}`}
              config={setting}
              page={activePage}
              sheet={activeSheet}
              layout={activeLayout}
              tableEntries={tableEntries}
            />
          ))}
        </div>
      </div>
    )
  })
}
